import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Google Analytics 4 (gtag.js) integration.
 *
 * - Loads the GA4 tag dynamically from `environment.gaTrackingId` and records a
 *   `page_view` on every SPA route change.
 * - No-ops when `gaTrackingId` is blank, so it never fires accidentally.
 *
 * GA4 measures acquisition (where traffic comes from); Mixpanel measures in-app
 * behaviour. They are complementary. The user app, partner dashboard and the
 * marketing site all share ONE GA4 property (same Measurement ID) so the whole
 * journey can be analysed together — break down by `hostname` in GA to view a
 * single site.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    private readonly router = inject(Router);

    init(): void {
        const id = environment.gaTrackingId;
        if (!id) return; // no-op when unset

        // Load the gtag.js library.
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        document.head.appendChild(script);

        // Canonical gtag shim — push the `arguments` object (NOT a plain array)
        // so gtag.js processes the command queue exactly as Google expects.
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        w.gtag = function () {
            w.dataLayer.push(arguments);
        };
        w.gtag('js', new Date());
        // Disable the automatic page_view; we emit one per SPA route change.
        w.gtag('config', id, { send_page_view: false });

        // Record a page_view on every completed navigation (incl. the first).
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => {
                w.gtag('event', 'page_view', {
                    page_path: e.urlAfterRedirects,
                    page_location: document.location.href,
                    page_title: document.title,
                });
            });
    }
}
